/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
import {
  apiInstance,
  BuyerCookieService,
  clearLocalStoragePreserveLocks,
  trackEvent,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS } from '@diamond/sol-buyer/constant';
import { useRouteRedirection } from '@diamond/sol-buyer/utils';
import * as SentryNext from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { createContext, ReactNode, useContext, useState } from 'react';
import shallow from 'zustand/shallow';

import { useAuthStore, useShipToStore } from './stores';

export type AuthSession = {
  session: null | {
    userId: string | null;
    userName: string | null;
    role: string | null;
    access_token: string | undefined;
  };
};

type AuthContext = {
  loggedIn: boolean;
  isLoading: boolean;
  login: (
    token: string,
    refreshToken: string,
    expTime: number
  ) => Promise<void>;
  logout: (token: string) => void;
} & AuthSession;

export const DefaultAuthContext: AuthContext = {
  session: null,
  loggedIn: false,
  isLoading: false,
  login: async () => {},
  logout: () => {},
};

export const AuthContext = createContext<AuthContext>(DefaultAuthContext);

export function AuthProvider({ children }: { children?: ReactNode }) {
  const [
    access_token,
    setUserId,
    setUserName,
    setUserRole,
    setAccessToken,
    setRefreshToken,
    setUser,
  ] = useAuthStore(
    (s) => [
      s.access_token,
      s.setUserId,
      s.setUserName,
      s.setUserRole,
      s.setAccessToken,
      s.setRefreshToken,
      s.setUser,
    ],
    shallow
  );

  const [setAvailableShipTo, setSelectedShipToId] = useShipToStore(
    (s) => [s.setAvailableShipTo, s.setSelectedShipToId],
    shallow
  );

  const router = useRouter();
  const { getPathnameRedirects } = useRouteRedirection();

  const isTokenExist =
    access_token !== null && access_token !== undefined && access_token !== '';
  const [loggedIn, setLoggedIn] = useState(isTokenExist);
  const [session, setSession] = useState<any>();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const login = async (
    token: string,
    refreshToken: string,
    expTime: number
  ) => {
    setIsLoading(true);
    try {
      const { data: userData } = await apiInstance(token).get('/account/me');
      const user_name = `${userData.first_name} ${userData.last_name}`;

      setUserId(userData.id);
      setUserName(user_name);
      setUserRole(userData.role);
      setAccessToken(token);
      setRefreshToken(refreshToken);

      setUser({
        id: userData.id,
        email: userData.email,
      });

      setAvailableShipTo(userData.ship_tos);
      setSelectedShipToId('');

      setSession({
        userId: userData.id,
        userName: user_name,
        role: userData.role,
        access_token: token,
      });

      BuyerCookieService.setAuthData(token, refreshToken, userData.role);

      await router
        .push({
          pathname: getPathnameRedirects(),
        })
        .then(() => {
          trackEvent(TRACKING_EVENTS.DIAMOND_VISIT, { platform: 'website' });
        })
        .catch((err) => {
          router.reload();
          SentryNext.captureException(new Error(err), (scope) => {
            scope.setTag('router', 'login');
            scope.setLevel('error');
            return scope;
          });
        });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Error decoding JWT:', error.message);
      throw new Error(error.message);
    } finally {
      setIsLoading(false);
      setLoggedIn(true);
    }
  };

  // Clear stored cookies and set false for loggedIn state.
  const logout = async (token: string) => {
    const response = await apiInstance(token).delete('/auth/logout');
    if (response.status === 204) {
      clearLocalStoragePreserveLocks();
      BuyerCookieService.clearAllAuthCookies();
      setLoggedIn(false);
      setUser(null);
      await router.push('/login');
    } else {
      throw new Error('something goes wrong');
    }
  };

  return (
    <AuthContext.Provider
      value={{ session, loggedIn, login, logout, isLoading }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthentication(): AuthContext {
  const context = useContext(AuthContext);
  return context;
}
